import { Logger } from '_common/services';

export class RichTextParser {
  private static debug: boolean = false;
  private static version = 3;

  static get getVersion(): number {
    return RichTextParser.version;
  }

  private static transformNode(node: any): RichTextJSON {
    return {
      type: node.type,
      content: (node.children || [])
        .map((child: any) => RichTextParser.transformChild(child))
        .filter(Boolean),
    };
  }

  private static transformChild(child: any): RichTextJSON | null {
    if (!child || typeof child !== 'object') return null;

    if (child.type === 'mention') {
      return { type: 'mention', attrs: { id: child.userId } };
    }

    if (!child.text) return null;

    const marks = Object.entries(child)
      .filter(([key]) => key !== 'text' && key !== 'type')
      .map(([key, value]) => ({
        type: key,
        ...(value && typeof value === 'object' ? { attrs: value } : {}),
      }));

    return { type: 'text', text: child.text, ...(marks.length ? { marks } : {}) };
  }

  private static parse(items: any): RichTextJSON[] {
    return items.map((node: any) => RichTextParser.transformNode(node));
  }

  static parseComments(
    comments: Presentation.Model.Comments.Data | null,
  ): Presentation.Model.Comments.Data | null {
    try {
      if (RichTextParser.debug) {
        Logger.info('parsing comments', comments);
      }

      let slides = comments?.sld;

      if (slides) {
        const sliderKeys = Object.keys(slides) as Array<keyof typeof slides>;

        for (let i = 0; i < sliderKeys.length; i++) {
          const sliderkey = sliderKeys[i];
          const slide = slides[sliderkey];

          const commentKeys = Object.keys(slide) as Array<keyof typeof slide>;

          for (let k = 0; k < commentKeys.length; k++) {
            const commentContent = slides[sliderkey][commentKeys[k]].content;
            // check content version
            if (
              commentContent.v !== RichTextParser.getVersion &&
              Array.isArray(commentContent.content)
            ) {
              slides[sliderkey][commentKeys[k]].content = {
                ...commentContent,
                content: {
                  type: 'doc',
                  content: RichTextParser.parse(commentContent.content),
                },
              };
            }

            // replies
            const numReplies = slides[sliderkey][commentKeys[k]].replies.length;
            if (numReplies) {
              for (let c = 0; c < numReplies; c++) {
                const replyContent = slides[sliderkey][commentKeys[k]].replies[c].content;
                // check reply version
                if (
                  replyContent.v !== RichTextParser.getVersion &&
                  Array.isArray(replyContent.content)
                ) {
                  slides[sliderkey][commentKeys[k]].replies[c].content = {
                    ...replyContent,
                    content: {
                      type: 'doc',
                      content: RichTextParser.parse(replyContent.content),
                    },
                  };
                }
              }
            }
          }
        }
      }

      if (RichTextParser.debug) {
        Logger.info('parsed comments', comments);
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return comments;
  }

  static parseTasks(
    tasks: Presentation.Model.Tasks.Data | null,
  ): Presentation.Model.Tasks.Data | null {
    try {
      if (RichTextParser.debug) {
        Logger.info('parsing tasks', tasks);
      }

      let slides = tasks?.sld;

      if (slides) {
        const sliderKeys = Object.keys(slides) as Array<keyof typeof slides>;

        for (let i = 0; i < sliderKeys.length; i++) {
          const sliderkey = sliderKeys[i];
          const slide = slides[sliderkey];

          const tasksKeys = Object.keys(slide) as Array<keyof typeof slide>;

          for (let k = 0; k < tasksKeys.length; k++) {
            const taskContent = slides[sliderkey][tasksKeys[k]].content;
            // check content version
            if (taskContent.v !== RichTextParser.getVersion && Array.isArray(taskContent.content)) {
              slides[sliderkey][tasksKeys[k]].content = {
                ...taskContent,
                content: {
                  type: 'doc',
                  content: RichTextParser.parse(taskContent.content),
                },
              };
            }

            // replies
            const numReplies = slides[sliderkey][tasksKeys[k]].replies.length;
            if (numReplies) {
              for (let c = 0; c < numReplies; c++) {
                const replyContent = slides[sliderkey][tasksKeys[k]].replies[c].content;
                // check reply version
                if (
                  replyContent.v !== RichTextParser.getVersion &&
                  Array.isArray(replyContent.content)
                ) {
                  slides[sliderkey][tasksKeys[k]].replies[c].content = {
                    ...replyContent,
                    content: {
                      type: 'doc',
                      content: RichTextParser.parse(replyContent.content),
                    },
                  };
                }
              }
            }
          }
        }
      }

      if (RichTextParser.debug) {
        Logger.info('parsed tasks', tasks);
      }
    } catch (error) {
      Logger.captureException(error);
    }

    return tasks;
  }
}
